import { NotImplementedError } from "../../../../errors";
import { parseDateFilter } from "../../../../models/looker/modules/overview";
import { PromiseHandler } from "../../../../handlers";
import { PLACEMENTS_QUERY, PLACEMENTS_REACH_BY_GROUP_QUERY, PLACEMENTS_TOTAL_REACH_QUERY } from "./query";
/**
 * Typed handler for the Overview dashboard Placements widget. Owns the LookerML
 * query construction, response typing, and caching so the frontend hook never
 * builds a raw query envelope (see the OA-131 review feedback that required all
 * overview fetches to go through typed SDK handlers).
 *
 * Reach is a distinct-user count and therefore not additive, so the group and
 * grand-total reach are queried at their own grains alongside the per-bundle
 * rows rather than summed on the client.
 */
export default class LookerOverviewPlacements extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-overview-placements");
    }
    async findItems(filters) {
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        // One Looker filter set shared by all three placements queries so the
        // bundle rows and the group/grand-total reach cover the same slice.
        const sharedFilters = {
            "explore_overview.flt_date_range": parseDateFilter(dateFilter)
        };
        const lookbackFilter = filters.where?.find((filter) => filter.field === "lookbackDays");
        if (lookbackFilter?.value !== undefined && lookbackFilter.value !== null) {
            sharedFilters["explore_overview.pmt_lookback_window"] = String(lookbackFilter.value);
        }
        const mediaTypeFilter = filters.where?.find((filter) => filter.field === "mediaType");
        if (mediaTypeFilter?.value) {
            sharedFilters["explore_overview.product_group"] = mediaTypeFilter.value;
        }
        // Fail closed: without advertiser/campaign ids the query would aggregate
        // placements across every partner. This only rejects missing ids — it
        // does not verify the caller owns the ids supplied, which is an existing
        // gap shared with the advertiser/campaign filter lookups this depends on.
        const advertiserFilter = filters.where?.find((filter) => filter.field === "advertiserIds");
        const advertiserIds = advertiserFilter?.value;
        if (!advertiserIds?.length) {
            throw new Error("Advertiser ids filter is required");
        }
        sharedFilters["advertisers.advertiser_id"] = advertiserIds.join(",");
        const campaignFilter = filters.where?.find((filter) => filter.field === "campaignIds");
        const campaignIds = campaignFilter?.value;
        if (!campaignIds?.length) {
            throw new Error("Campaign ids filter is required");
        }
        sharedFilters["campaigns.campaign_id"] = campaignIds.join(",");
        const rowsQuery = { ...PLACEMENTS_QUERY, filters: { ...sharedFilters } };
        const reachByGroupQuery = {
            ...PLACEMENTS_REACH_BY_GROUP_QUERY,
            filters: { ...sharedFilters }
        };
        const totalReachQuery = {
            ...PLACEMENTS_TOTAL_REACH_QUERY,
            filters: { ...sharedFilters }
        };
        const hashKey = await this.sdk.looker.query.hashQuery({
            rowsQuery,
            reachByGroupQuery,
            totalReachQuery
        });
        return this.cache.promise(hashKey, async () => {
            // The bundle-rows query backs the whole widget, so its failure fails the
            // widget (the behavior before reach was added). The two reach queries are
            // best-effort: a failure leaves that reach undefined (rendered as an em
            // dash) rather than taking the placement rows down with it.
            const rowsRes = await this.sdk.looker.query.run(rowsQuery).catch((e) => {
                console.error(e);
                throw new Error("Failed to fetch overview dashboard placements");
            });
            const [reachByGroupRes, totalReachRes] = await Promise.allSettled([
                this.sdk.looker.query.run(reachByGroupQuery),
                this.sdk.looker.query.run(totalReachQuery)
            ]);
            return [
                {
                    data: {
                        rows: this.formatResponse(rowsRes.data),
                        reachByGroup: reachByGroupRes.status === "fulfilled"
                            ? this.formatReachByGroup(reachByGroupRes.value.data)
                            : undefined,
                        totalReach: totalReachRes.status === "fulfilled"
                            ? this.formatTotalReach(totalReachRes.value.data)
                            : undefined
                    },
                    queryMeta: rowsRes.queryMeta
                }
            ];
        });
    }
    formatResponse(data) {
        return data
            .filter((row) => row["explore_overview.product_group"])
            .map((row) => ({
            productGroup: row["explore_overview.product_group"],
            bundle: row["explore_overview.bundle"] || "",
            impressions: row["explore_overview.total_impressions"] || 0,
            retargetingImpressions: row["explore_overview.retargeting_impressions"] || 0,
            reach: row["explore_overview.total_reach"] || 0,
            clicks: row["explore_overview.total_clicks"] || 0,
            clickRate: row["explore_overview.click_rate"] || 0
        }));
    }
    formatReachByGroup(data) {
        const reachByGroup = {};
        for (const row of data) {
            const group = row["explore_overview.product_group"];
            if (!group) {
                continue;
            }
            reachByGroup[group] = row["explore_overview.total_reach"] || 0;
        }
        return reachByGroup;
    }
    // Undefined (not 0) when the query returned no reach, so the widget can show
    // an em dash rather than a misleading 0. A real 0 reach is preserved.
    formatTotalReach(data) {
        return data[0]?.["explore_overview.total_reach"] ?? undefined;
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
//# sourceMappingURL=Placements.js.map